import { getSlotId, groupByTimeSlots } from './utils';
import { URL_REGEXP_ATHID, urlRegExp } from './constants';
import { WorkerManager } from './worker-manager';
import client_settings from '../client_settings';

const DEBUG = false;
const logger = (...content) => DEBUG && console.log(content);

/**
 * Packs and processes data from a given URL in an optimized manner by downloading and decoding the data.
 *
 * This function initializes a `WorkerManager` instance to handle the download and decoding of the data
 * from the specified URL.
 * Once processing is complete or an error occurs, the worker is terminated.
 *
 * @param {string} sessionId - The identifier for the current session.
 * @param {string} athleteId - The identifier of the athlete for whom the data is being processed.
 * @param {string} url - The URL from which the data will be downloaded and decoded.
 * @returns {Promise<void>}A promise that resolves once the download and decoding processes are complete.
 */
const packUrlData = async (sessionId: string, athleteId: string, url: string): Promise<void> => {
  const workerManager = new WorkerManager();

  try {
    console.log('🚀 Avvio download e decodifica ottimizzata...');
    const now = performance.now();
    const result = await workerManager.downloadFromUrlAndDecodePack(
      sessionId,
      athleteId,
      url,
      { format: 'binary' },
      (progress) => {
        console.log(`📊 ${progress.message} (${progress.progress.toFixed(1)}%)`);
      }
    );

    const time = performance.now() - now;

    console.log(`✅ Completato in ${time} ms`, result);
  } catch (error) {
    console.error('❌ Errore:', error);
  } finally {
    workerManager.terminate();
  }
}

/**
 * Processes an array of athlete session strings and packs relevant data for each session.
 *
 * This function iterates through a list of athlete sessions, extracts session identifiers
 * based on a predefined URL regular expression, and processes the data using the `packUrlData` method
 * if a valid session identifier is found.
 *
 * @param {string[]} athSessions - An array of athlete session strings, which may contain identifiable session information.
 * @param {string} sessionId - The session identifier to be processed and associated with the athlete sessions.
 * @param onProgress
 * @param onCompleted
 */
const packData = async (
  athSessions: string[],
  sessionId: string,
  onProgress?: (id: string) =>void,
  onCompleted?: () => void,
) => {
logger('packData', athSessions, sessionId)
  try {
    for (const athSession of athSessions) { // slice(0,2)
      const athSessionId = urlRegExp.test(athSession) ? athSession.match(urlRegExp)?.[URL_REGEXP_ATHID] : null;
      logger('packData - ', sessionId, athSessionId, athSession);

      if (athSessionId) {
        await packUrlData(
          sessionId,
          athSessionId,
          athSession
        );

        if (onProgress) {
          onProgress(athSessionId);
        }
      }
    }

    if (onCompleted) {
      onCompleted();
    }
  } catch (e) {
    logger(e)
    return false;
  }

  return true;
}

/**
 * Verifica la disponibilità dei dati del visualizzatore associati a una sessione specificata.
 *
 * Questa funzione asincrona effettua una richiesta al backend per verificare se
 * i dati del visualizzatore sono disponibili per un determinato ID sessione. Richiede un token
 * di autenticazione per eseguire la richiesta.
 *
 * @param sessionId - L'identificatore della sessione per controllare i dati del visualizzatore.
 * @returns Una promise che si risolve in un oggetto contenente il codice di stato HTTP
 *          e un booleano che indica se i dati del visualizzatore sono disponibili.
 * @throws Genera un errore se la richiesta HTTP al backend fallisce o restituisce un
 *         codice di stato non-OK.
 */
const checkSessionDataAvailable = async (sessionId: string): Promise<{
  status: number,
  available: boolean,
  processing: boolean,
}> => {
  const token = localStorage.getItem('exelio_token');

  if (!token) {
    throw new Error(`Anomalia - No Token`); // @todo logout?
  }

  let resp: Response;

  try {
    resp = await fetch(
      `${client_settings.public.gpexeBackendUrl}rest/v2/session/team/${sessionId}/check_viewer_data/`,
      {
        headers: {
          'Authorization': `JWT ${token}`,
        }
      }
    );
  } catch (e) {
    console.error('Errore nella verifica dati disponibili', e);
    throw new Error(`HTTP error!`);
  }

  const respData = await resp.json();

  return {
    status: resp.status,
    available: respData.available,
    processing: respData.processing,
  }
}

/**
 * Asynchronously loads session data for a given session ID and authentication token.
 *
 * This function fetches viewer data for a specific session and ensures
 * proper authentication using a provided JWT token. If the request is
 * successful, it returns the HTTP status and a list of uploaded files
 * associated with the session. In case of an error, it throws an exception
 * indicating the HTTP error status.
 *
 * @param sessionId The session identifier for which the data will be loaded.
 * @param token The JWT authentication token used to access session data.
 * @returns An object containing the HTTP status and session-related data, such as files uploaded.
 * @throws An error if the HTTP response status is not successful.
 */
const loadSessionData = async (sessionId: string, token: string) => {
  const resp = await fetch(
    `${client_settings.public.gpexeBackendUrl}rest/v2/session/team/${sessionId}/enqueue_viewer_data/`,
    {
      headers: {
        'Authorization': `JWT ${token}`,
      }
    }
  );

  if (!resp.ok) {
    throw new Error(`HTTP error! status: ${resp.status}`);
  }

  const respData = await resp.json();

  return {
    status: resp.status,
    athSessions: respData.files_uploaded || respData,
  }
}

/**
 * Asynchronously loads and processes data for a specific session.
 *
 * This function creates a `WorkerManager` instance to handle the data loading process.
 * It performs reading and decoding of the data block associated with the given `sessionId` and `timestamp`.
 * After successfully processing the data, it groups the output into time slots and returns the result.
 * The worker manager is terminated in a `finally` block to ensure cleanup of resources.
 *
 * @param sessionId - Identifier for the session whose data needs to be loaded.
 * @param timestamp - The timestamp indicating the specific time block to load data for.
 * @returns Processed output grouped by time slots.
 * @throws Any error occurring during the data loading process is logged.
 */
const loadData = async (sessionId: string, timestamp: number) => {
  const workerManager = new WorkerManager();

  try {
    console.log('🚀 Lettura e decodifica...', timestamp);
    const now = performance.now();
    const out = await workerManager.loadDataBlock(
      sessionId,
      timestamp,
    );

    // filtrare per timestamp

    const data = groupByTimeSlots(out.output);

    const time = performance.now() - now;

    logger(`✅ Completato in ${time} ms`);
    return data;
  } catch (error) {
    console.error('❌ Errore:', error);
  } finally {
    logger('terminate')
    workerManager.terminate();
  }
}

const isDataAvailable = async (sessionId: string) => {
  try {
    console.log('🚀 Verifica dati disponibili...');
    const now = performance.now();

    const result = await WorkerManager.isDataAvailable(sessionId);

    console.log('isDataAvailable', result);

    const time = performance.now() - now;

    console.log(`✅ Completato in ${time} ms`);
    return result.isDataAvailable;
  } catch (error) {
    console.error('❌ Errore:', error);
    return false;
  }
}

/**
 * Retrieves player data based on the provided timestamp and state object.
 *
 * This function determines the slot identifier associated with the given
 * timestamp and checks if the state object contains data for the respective slot.
 *
 * @param timestamp The specific timestamp used to retrieve player data.
 * @param state The state object containing player data organized by slot and timestamp.
 */
const getPlayersData = (timestamp: number, state: any)=> {
  const slotId = getSlotId(timestamp);
debugger;
  console.log('getPlayersData', timestamp, slotId, state)

  if (!state || !state[slotId]) {
    console.log('no data')
  } else {
    console.log('dati trovati', state[slotId], state[slotId][timestamp]);
    console.log('dati players', Object.keys(state[slotId]))
    console.log('dati path', Object.keys(state[slotId]).map((player) => state[slotId][player][timestamp]))
    console.log('debug timestamps', Object.keys(state[slotId]).map((player) => Object.keys(state[slotId][player]).slice(0, 10)))
  }
}

export {
  checkSessionDataAvailable,
  loadData,
  loadSessionData,
  getPlayersData,
  packData,
  isDataAvailable,
}
